import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, eur } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, ShieldCheck, AlertTriangle, XCircle, Save, Loader2, Home, Flame, Lock, Sun, Scale, PawPrint, LifeBuoy, Info } from "lucide-react";

const SEZIONI = [
  { code: "incendio", label: "Incendio e altri danni ai beni", icon: Flame },
  { code: "furto", label: "Furto e Rapina", icon: Lock },
  { code: "energie", label: "Energie Rinnovabili", icon: Sun },
  { code: "rc", label: "Responsabilità Civile", icon: ShieldCheck },
  { code: "tutela_legale", label: "Tutela Legale", icon: Scale },
  { code: "pet", label: "Pet", icon: PawPrint },
  { code: "assistenza", label: "Assistenza", icon: LifeBuoy },
];
const SEZ_LABEL = { incendio: "Incendio", furto: "Furto e Rapina", energie: "Energie Rinnovabili", rc: "Responsabilità Civile", tutela_legale: "Tutela Legale", pet: "Pet", assistenza: "Assistenza" };

// Campi definiti a livello di MODULO (non dentro il componente) per non perdere il focus a ogni digitazione.
function NumField({ k, label, placeholder, input, setI }) {
  const v = input[k] === "" || input[k] == null ? "" : input[k];
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="number" data-testid={`casa-${k}`} placeholder={placeholder || "€"} value={v} onChange={(e) => setI(k, e.target.value)} />
    </div>
  );
}
function Check({ k, label, input, setI }) {
  return (
    <label className="flex items-center gap-2 text-sm text-forest">
      <Switch data-testid={`casa-${k}`} checked={!!input[k]} onCheckedChange={(val) => setI(k, val)} /> {label}
    </label>
  );
}

export default function CasaQuoteEngine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [config, setConfig] = useState(null);
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const requestId = params.get("request");
  const editId = params.get("edit");
  const [editLoaded, setEditLoaded] = useState(false);

  const [sezioni, setSezioni] = useState({ incendio: true });
  const [input, setInput] = useState({ rc_massimale: 2000000, tl_formula: "light", tl_massimale: 15000, pet_formula: "base", energie_zona: 1 });
  const [slider, setSlider] = useState(0);
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));
  const [frazionamento, setFrazionamento] = useState("annuale");
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/casa/config").then(({ data }) => setConfig(data));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data));
  }, []);

  useEffect(() => {
    if (!config || !editId || editLoaded) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      const i = q.casa_input || {};
      setAnagraficaId(q.anagrafica_id || "");
      if (i.sezioni) setSezioni(i.sezioni);
      if (i.input) setInput(i.input);
      setSlider(i.slider_pct || 0);
      if (i.data_effetto) setDataEffetto(i.data_effetto);
      if (i.frazionamento) setFrazionamento(i.frazionamento);
      setEditLoaded(true);
      toast.info("Modalità modifica: aggiorna i dati e salva per ricalcolare");
    }).catch(() => setEditLoaded(true));
  }, [config, editId, editLoaded]);

  const setI = (k, v) => setInput((p) => ({ ...p, [k]: v }));
  const toggleSez = (code) => setSezioni((s) => ({ ...s, [code]: !s[code] }));

  const buildPayload = () => ({ sezioni, input, slider_pct: slider, data_effetto: dataEffetto, frazionamento });

  const calculate = async () => {
    setCalculating(true);
    try { const { data } = await api.post("/casa/quotes/calculate", buildPayload()); setResult(data); }
    catch (e) { toast.error(apiError(e)); } finally { setCalculating(false); }
  };
  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona un'anagrafica");
    setSaving(true);
    try {
      const { data } = await api.post("/casa/quotes", { ...buildPayload(), anagrafica_id: anagraficaId, request_id: requestId, quote_id: editId || undefined });
      toast.success(editId ? "Preventivo aggiornato" : "Preventivo salvato");
      navigate(isAdmin ? `/admin/preventivi/${data.id}` : `/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const statusBanner = useMemo(() => {
    if (!result) return null;
    if (result.status === "non_ammissibile")
      return { icon: XCircle, tone: "bg-red-50 border-red-200 text-red-800", title: "Preventivo non calcolabile", msgs: result.eligibility.blocking_errors };
    if (result.binario === "referral_tecnico")
      return { icon: AlertTriangle, tone: "bg-amber-50 border-amber-200 text-amber-800", title: "Referral tecnico / quotazione manuale", msgs: result.eligibility.warnings || [] };
    return { icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Preventivo calcolabile", msgs: result.eligibility.warnings || [] };
  }, [result]);

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const premium = result?.premium;
  const flexMax = config.flessibilita_max?.eticapro || 20;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="casa-quote-engine">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold text-forest">
          <Home className="h-7 w-7 text-terracotta" /> Preventivo Casa — Tutela Multirischi per l'Abitazione
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{config.product_version} · calcolo lato server. Terremoto/Alluvione ed Eventi atmosferici: quotazione manuale (in arrivo).</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="rounded-xl border-border/60 p-5">
            <Label className="mb-1 block text-sm font-medium text-forest">Cliente / Anagrafica</Label>
            <Select value={anagraficaId} onValueChange={setAnagraficaId}>
              <SelectTrigger data-testid="casa-anagrafica"><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
              <SelectContent>
                {anagrafiche.map((a) => <SelectItem key={a.id} value={a.id}>{a.ragione_sociale}</SelectItem>)}
              </SelectContent>
            </Select>
          </Card>

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Sezioni</h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {SEZIONI.map((s) => {
                const Icon = s.icon;
                const on = !!sezioni[s.code];
                return (
                  <button key={s.code} data-testid={`casa-sez-${s.code}`} onClick={() => toggleSez(s.code)}
                    className={`flex items-center gap-2 rounded-xl border p-3 text-left text-sm transition-colors ${on ? "border-terracotta bg-terracotta/10 text-forest" : "border-border/60 text-muted-foreground hover:bg-sage/40"}`}>
                    <Icon className={`h-4 w-4 ${on ? "text-terracotta" : ""}`} /> {s.label}
                  </button>
                );
              })}
            </div>
          </Card>

          {sezioni.incendio && (
            <Card className="rounded-xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-forest"><Flame className="h-4 w-4 text-terracotta" /> Incendio e altri danni ai beni</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <NumField k="incendio_fabbricato" label="Fabbricato — somma assicurata" input={input} setI={setI} />
                <NumField k="incendio_contenuto" label="Contenuto (P.R.A.)" input={input} setI={setI} />
                <NumField k="incendio_rischio_locativo" label="Rischio locativo" input={input} setI={setI} />
                <NumField k="incendio_danni_acqua" label="Danni d'acqua — somma" input={input} setI={setI} />
                <NumField k="incendio_danni_terzi" label="Danni a terzi — somma" input={input} setI={setI} />
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Check k="danni_acqua_10k" label="Danni d'acqua: ricerca fino a 10.000 (+15€)" input={input} setI={setI} />
                <Check k="incendio_fenomeno_elettrico" label="Fenomeno elettrico" input={input} setI={setI} />
                {input.incendio_fenomeno_elettrico && <NumField k="incendio_fenomeno_elettrico_sa" label="Fenomeno elettrico — somma" input={input} setI={setI} />}
                <Check k="incendio_danni_tubi_interrati" label="Danni da tubi interrati" input={input} setI={setI} />
                {input.incendio_danni_tubi_interrati && <NumField k="incendio_danni_tubi_interrati_sa" label="Tubi interrati — somma" input={input} setI={setI} />}
              </div>
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-800"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Eventi atmosferici, Terremoto e Alluvione: quotazione manuale (tabelle in arrivo). Se servono, la pratica va in referral.</p>
            </Card>
          )}

          {sezioni.furto && (
            <Card className="rounded-xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-forest"><Lock className="h-4 w-4 text-terracotta" /> Furto e Rapina</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <NumField k="furto_contenuto" label="Furto Contenuto — somma" input={input} setI={setI} />
                <NumField k="furto_scippo" label="Scippo — somma" input={input} setI={setI} />
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Check k="furto_aumento_limiti" label="Aumento limiti indennizzo (+20%)" input={input} setI={setI} />
                <Check k="furto_potenziamento" label="Potenziamento mezzi di chiusura (18€)" input={input} setI={setI} />
              </div>
            </Card>
          )}

          {sezioni.energie && (
            <Card className="rounded-xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-forest"><Sun className="h-4 w-4 text-terracotta" /> Energie Rinnovabili</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Zona tariffaria FV (1-6)</Label>
                  <Select value={String(input.energie_zona || 1)} onValueChange={(v) => setI("energie_zona", Number(v))}>
                    <SelectTrigger data-testid="casa-energie_zona"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5, 6].map((z) => <SelectItem key={z} value={String(z)}>Zona {z}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <NumField k="energie_fv" label="Fotovoltaico/pannelli — somma" input={input} setI={setI} />
                <NumField k="energie_batterie" label="Batterie di accumulo — somma" input={input} setI={setI} />
                <NumField k="energie_furto_fv" label="Furto FV/colonnine — somma" input={input} setI={setI} />
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Check k="energie_eolico" label="Impianto eolico uso domestico (12€)" input={input} setI={setI} />
                <Check k="energie_colonnine" label="Colonnine di ricarica (22€)" input={input} setI={setI} />
              </div>
            </Card>
          )}

          {sezioni.rc && (
            <Card className="rounded-xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-forest"><ShieldCheck className="h-4 w-4 text-terracotta" /> Responsabilità Civile</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Tipo RC</Label>
                  <Select value={input.rc_tipo || ""} onValueChange={(v) => setI("rc_tipo", v)}>
                    <SelectTrigger data-testid="casa-rc_tipo"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{config.rc_tipi.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Massimale</Label>
                  <Select value={String(input.rc_massimale || 1000000)} onValueChange={(v) => setI("rc_massimale", Number(v))}>
                    <SelectTrigger data-testid="casa-rc_massimale"><SelectValue /></SelectTrigger>
                    <SelectContent>{config.rc_massimali.map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Check k="rc_consulenza_legale" label="Consulenza legale telefonica (6€)" input={input} setI={setI} />
                <Check k="rc_assistenza_mobilita" label="Assistenza mobilità (16,59€)" input={input} setI={setI} />
                <Check k="rc_violazione_privacy" label="Violazione privacy (5€)" input={input} setI={setI} />
                <Check k="rc_beb" label="RC esercizio B&B/Affittacamere" input={input} setI={setI} />
              </div>
            </Card>
          )}

          {sezioni.tutela_legale && (
            <Card className="rounded-xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-forest"><Scale className="h-4 w-4 text-terracotta" /> Tutela Legale</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Formula</Label>
                  <Select value={input.tl_formula || "light"} onValueChange={(v) => setI("tl_formula", v)}>
                    <SelectTrigger data-testid="casa-tl_formula"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="light">LIGHT</SelectItem><SelectItem value="full">FULL</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Massimale</Label>
                  <Select value={String(input.tl_massimale || 15000)} onValueChange={(v) => setI("tl_massimale", Number(v))}>
                    <SelectTrigger data-testid="casa-tl_massimale"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="15000">15.000 €</SelectItem><SelectItem value="25000">25.000 €</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              {input.tl_formula === "full" && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {config.tl_estensioni.map((e) => <Check key={e.code} k={`tl_${e.code}`} label={e.label} input={input} setI={setI} />)}
                </div>
              )}
            </Card>
          )}

          {sezioni.pet && (
            <Card className="rounded-xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-forest"><PawPrint className="h-4 w-4 text-terracotta" /> Pet</h3>
              <div className="mb-3 w-full sm:w-48">
                <Label className="text-xs">Formula</Label>
                <Select value={input.pet_formula || "base"} onValueChange={(v) => setI("pet_formula", v)}>
                  <SelectTrigger data-testid="casa-pet_formula"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="base">BASE</SelectItem><SelectItem value="top">TOP</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {config.pet_garanzie.map((g) => <Check key={g.code} k={`pet_${g.code}`} label={g.label} input={input} setI={setI} />)}
              </div>
            </Card>
          )}

          {sezioni.assistenza && (
            <Card className="rounded-xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-forest"><LifeBuoy className="h-4 w-4 text-terracotta" /> Assistenza</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {config.assistenza.map((a) => <Check key={a.code} k={`assistenza_${a.code}`} label={a.label} input={input} setI={setI} />)}
              </div>
            </Card>
          )}

          <Card className="rounded-xl border-border/60 p-5">
            <h3 className="mb-3 font-semibold text-forest">Sconti automatici (da regole tariffarie)</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {config.sconti_regole.map((r) => <Check key={r.code} k={`sconto_${r.code}`} label={r.label} input={input} setI={setI} />)}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="sticky top-4 rounded-xl border-border/60 p-5">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest"><Calculator className="h-5 w-5 text-terracotta" /> Premio</h2>

            <div className="mb-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Flessibilità (sconto)</span>
                <span data-testid="casa-slider-value" className="font-heading text-xl font-semibold text-terracotta">−{slider}%</span>
              </div>
              <Slider data-testid="casa-slider" className="mt-2" value={[slider]} min={0} max={flexMax} step={1} onValueChange={(v) => setSlider(v[0])} />
            </div>

            <Button data-testid="casa-calcola" onClick={calculate} disabled={calculating} className="mb-3 w-full gap-2 rounded-full bg-forest hover:bg-forest/90">
              {calculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Calcola premio
            </Button>

            {statusBanner && (
              <div className={`mb-3 rounded-lg border p-3 text-xs ${statusBanner.tone}`} data-testid="casa-status">
                <div className="flex items-center gap-1.5 font-semibold"><statusBanner.icon className="h-4 w-4" /> {statusBanner.title}</div>
                {statusBanner.msgs?.length > 0 && <ul className="mt-1 list-disc pl-4">{statusBanner.msgs.map((m, i) => <li key={i}>{m}</li>)}</ul>}
              </div>
            )}

            {premium && (
              <div className="space-y-1.5 text-sm" data-testid="casa-premium">
                {Object.entries(result.sezioni || {}).map(([code, s]) => (
                  <div key={code} className="flex justify-between">
                    <span className="text-muted-foreground">{SEZ_LABEL[code] || code}{s.sconto_auto_pct ? ` (−${s.sconto_auto_pct}%)` : ""}</span>
                    <span className="text-forest">{eur(s.gross_total)}</span>
                  </div>
                ))}
                <div className="mt-2 flex justify-between border-t border-border pt-2 font-heading text-lg font-semibold text-forest">
                  <span>Premio annuo lordo</span><span data-testid="casa-premio-finale">{eur(premium.gross_total)}</span>
                </div>
                {premium.minimum_premium_applied && <p className="text-xs text-muted-foreground">Applicato premio minimo di polizza.</p>}
                {(result.sconti_automatici || []).length > 0 && (
                  <p className="text-xs text-emerald-700">Sconti applicati: {result.sconti_automatici.map((s) => s.label).join(", ")}</p>
                )}
              </div>
            )}

            <Button data-testid="casa-save-quote-button" onClick={save} disabled={saving || !anagraficaId || !result || result.status === "non_ammissibile"}
              className="mt-4 w-full gap-2 rounded-full bg-terracotta hover:bg-terracotta/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {editId ? "Aggiorna preventivo" : "Salva preventivo"}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
